import { computed, inject, Injectable } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { AccountInfo } from '@azure/msal-browser';
import {
  getUserInitialsFromAccountInfo,
  isCanvasReadonlyMode,
} from '@seu-helpers';
import {
  SeuActiveCalculation,
  SeuBreakingChangesItem,
  SeuCalculationResponseItem,
  SeuCanvasViewMode,
  SeuComputationCombination,
  SeuConfig,
  SeuConfigTranslationObject,
  SeuConfigVersion,
  SeuInterconnection,
  SeuLanguage,
  SeuOffer,
  SeuRevision,
  SeuUser,
  SeuUserProfile,
} from '@seu-types';
import { first } from 'lodash-es';
import {
  BehaviorSubject,
  debounceTime,
  distinctUntilChanged,
  filter,
} from 'rxjs';
import { StorageService } from './storage.service';

@Injectable({
  providedIn: 'root',
})
export class StoreService {
  private readonly storageService = inject(StorageService);

  private readonly user$$ = new BehaviorSubject<SeuUser | null | undefined>(
    undefined
  );

  public readonly user$ = this.user$$.asObservable().pipe(
    filter((user): user is SeuUser | null => user !== undefined),
    distinctUntilChanged((a, b) => JSON.stringify(a) === JSON.stringify(b))
  );

  private readonly userProfile$$ = new BehaviorSubject<
    SeuUserProfile | null | undefined
  >(undefined);

  public readonly userProfile$ = this.userProfile$$.asObservable().pipe(
    filter(
      (profile): profile is SeuUserProfile | null => profile !== undefined
    ),
    distinctUntilChanged((a, b) => JSON.stringify(a) === JSON.stringify(b))
  );

  private readonly activeOffer$$ = new BehaviorSubject<
    SeuOffer | undefined | null
  >(undefined);
  private readonly lastOpenedOffer$ = new BehaviorSubject<SeuOffer | undefined>(
    undefined
  );

  public readonly activeOffer$ = this.activeOffer$$.pipe(
    filter((offer): offer is SeuOffer | null => offer !== undefined),
    debounceTime(10),
    distinctUntilChanged((a, b) => JSON.stringify(a) === JSON.stringify(b))
  );

  private readonly activeInterconnection$$ = new BehaviorSubject<
    SeuInterconnection | undefined | null
  >(undefined);

  public readonly activeInterconnection$ = this.activeInterconnection$$.pipe(
    filter(
      (interconnection): interconnection is SeuInterconnection | null =>
        interconnection !== undefined
    )
  );

  private readonly config$$ = new BehaviorSubject<SeuConfig | undefined>(
    undefined
  );

  public readonly config$ = this.config$$.pipe(
    distinctUntilChanged((a, b) => JSON.stringify(a) === JSON.stringify(b))
  );

  private readonly computationCombinations$$ = new BehaviorSubject<
    SeuComputationCombination[] | null
  >(null);

  public readonly computationCombinations$ =
    this.computationCombinations$$.asObservable();

  private readonly activeCalculations$$ = new BehaviorSubject<
    SeuActiveCalculation[]
  >([]);

  public readonly activeCalculations$ =
    this.activeCalculations$$.asObservable();

  private readonly calculationResponseItems$$ = new BehaviorSubject<
    SeuCalculationResponseItem[]
  >([]);

  public readonly calculationResponseItems$ =
    this.calculationResponseItems$$.asObservable();

  private readonly activeCalculationResponseItem$$ =
    new BehaviorSubject<SeuCalculationResponseItem | null>(null);

  public readonly activeCalculationResponseItem$ =
    this.activeCalculationResponseItem$$.asObservable();

  private readonly canvasViewMode$$ = new BehaviorSubject<SeuCanvasViewMode>(
    'edit'
  );

  public readonly canvasViewMode$ = this.canvasViewMode$$.asObservable();

  private readonly revisions$$ = new BehaviorSubject<SeuRevision[]>([]);

  public readonly revisions$ = this.revisions$$.asObservable();

  private readonly offerSignal = toSignal(this.activeOffer$);

  private readonly canvasViewModeSignal = toSignal(this.canvasViewMode$);

  public readonly isReadonlyMode = computed(() => {
    const offer = this.offerSignal();
    if (offer?.isRevision) {
      return true;
    }

    const canvasViewMode = this.canvasViewModeSignal();
    return canvasViewMode === 'results';
  });

  public readonly isCanvasReadonlyMode = computed(() => {
    if (this.isReadonlyMode()) {
      return true;
    }

    const canvasViewMode = this.canvasViewModeSignal();
    return isCanvasReadonlyMode(canvasViewMode);
  });

  private readonly breakingChanges$$ = new BehaviorSubject<
    SeuBreakingChangesItem[]
  >([]);

  public readonly breakingChanges$ = this.breakingChanges$$.asObservable();

  public setUserFromAccountInfo(accountInfo: AccountInfo | null): void {
    if (!accountInfo) {
      this.user$$.next(null);
      return;
    }
    this.user$$.next({
      accountInfo,
      name: accountInfo.name ?? '',
      initials: getUserInitialsFromAccountInfo(accountInfo),
    });
  }

  public setUser(user: SeuUser | null): void {
    this.user$$.next(user);
  }

  public getUser(): SeuUser | null | undefined {
    return this.user$$.getValue();
  }

  public setUserProfile(userProfile: SeuUserProfile | null): void {
    this.userProfile$$.next(userProfile);
  }

  public getUserProfile(): SeuUserProfile | null | undefined {
    return this.userProfile$$.getValue();
  }

  public setActiveOffer(activeOffer: SeuOffer | null): void {
    this.activeOffer$$.next(activeOffer);
  }

  public setActiveInterconnection(
    activeInterconnection?: SeuInterconnection | null
  ): void {
    const prevId = this.activeInterconnection$$.getValue()?.id;

    if (activeInterconnection) {
      this.activeInterconnection$$.next(activeInterconnection);
    } else {
      const currentActiveInterconnectionId =
        this.activeInterconnection$$.getValue()?.id;
      if (currentActiveInterconnectionId) {
        const offer = this.activeOffer$$.getValue();
        const updatedActiveInterconnection = offer?.interconnections.find(
          (interconnection) =>
            interconnection.id === currentActiveInterconnectionId
        );
        this.activeInterconnection$$.next(updatedActiveInterconnection ?? null);
      } else {
        const firstInterconnection = first(
          this.activeOffer$$.getValue()?.interconnections
        );
        this.activeInterconnection$$.next(firstInterconnection ?? null);
      }
    }

    const fallBackMode: SeuCanvasViewMode = this.offerSignal()?.isRevision
      ? 'present'
      : 'edit';

    const newId = this.activeInterconnection$$.getValue()?.id;
    if (newId && newId !== prevId) {
      const stored =
        this.storageService.getSessionItem<Record<string, SeuCanvasViewMode>>(
          'canvas-view-modes'
        ) ?? {};
      let restoredMode: SeuCanvasViewMode = stored[newId] ?? fallBackMode;
      if (restoredMode === 'results') {
        const hasValidResults = this.calculationResponseItems$$
          .getValue()
          .some(
            (item) =>
              item.interconnectionId === newId &&
              item.computationState !== 'error'
          );
        if (!hasValidResults) {
          restoredMode = fallBackMode;
        }
      }
      this.canvasViewMode$$.next(restoredMode);
    }
  }

  public setComputationCombinations(
    computationCombinations: SeuComputationCombination[]
  ): void {
    this.computationCombinations$$.next(computationCombinations);
  }

  public updateActiveInterconnection(
    activeInterconnection: SeuInterconnection | null
  ): void {
    const curInterconnection = this.activeInterconnection$$.getValue();
    if (curInterconnection?.id === activeInterconnection?.id) {
      this.activeInterconnection$$.next(activeInterconnection);
    }
  }

  public setConfig(config: SeuConfig): void {
    this.config$$.next(config);
  }

  public getConfig(): SeuConfig {
    return this.config$$.getValue() as SeuConfig;
  }

  public setBreakingChanges(breakingChanges: SeuBreakingChangesItem[]): void {
    this.breakingChanges$$.next(breakingChanges);
  }

  public getCurrentVersion(): number {
    const config = this.getConfig();
    return config.versionNumber;
  }

  public getAllVersions(): SeuConfigVersion[] {
    const config = this.getConfig();
    const changedConfigs = config.changedConfigs ?? {};
    return Object.keys(changedConfigs)
      .sort()
      .map((version) => ({
        versionNr: Number(version),
        changedElements: [
          ...(changedConfigs[version]?.components ?? []),
          ...(changedConfigs[version]?.globals ?? []),
        ],
      }));
  }

  public getConfigTranslations(
    lang: SeuLanguage
  ): SeuConfigTranslationObject | undefined {
    const config = this.getConfig();
    return config.translations[lang];
  }

  public setActiveCalculations(
    activeCalculations: SeuActiveCalculation[]
  ): void {
    this.activeCalculations$$.next(activeCalculations);
  }

  public addActiveCalculation(activeCalculation: SeuActiveCalculation): void {
    const activeCalculations = this.activeCalculations$$.getValue();
    this.activeCalculations$$.next([...activeCalculations, activeCalculation]);
  }

  public removeActiveCalculationsByGroupId(groupId: string): void {
    const activeCalculations = this.activeCalculations$$.getValue();
    this.activeCalculations$$.next(
      activeCalculations.filter((calc) => calc.groupId !== groupId)
    );
  }

  public addCalculationResponseItem(
    calculationResponseItem: SeuCalculationResponseItem
  ): void {
    const calculationResponseItems = this.calculationResponseItems$$.getValue();
    this.calculationResponseItems$$.next([
      ...calculationResponseItems,
      calculationResponseItem,
    ]);
  }

  public setCalculationResponseItems(
    calculationResponseItems: SeuCalculationResponseItem[]
  ): void {
    this.calculationResponseItems$$.next(calculationResponseItems);
  }

  public resetCalculationResponseItems(): void {
    this.calculationResponseItems$$.next([]);
  }

  public setActiveCalculationResponseItem(
    activeCalculationResponseItem: SeuCalculationResponseItem | null
  ): void {
    this.activeCalculationResponseItem$$.next(activeCalculationResponseItem);
  }

  public setCanvasViewMode(mode: SeuCanvasViewMode): void {
    this.canvasViewMode$$.next(mode);

    const interconnectionId = this.activeInterconnection$$.getValue()?.id;
    if (interconnectionId) {
      const stored =
        this.storageService.getSessionItem<Record<string, SeuCanvasViewMode>>(
          'canvas-view-modes'
        ) ?? {};
      stored[interconnectionId] = mode;
      this.storageService.setSessionItem('canvas-view-modes', stored);
    }
  }

  public setRevisions(revisions: SeuRevision[]): void {
    this.revisions$$.next(revisions);
  }
}
